import { useTranslation } from "react-i18next";
import { CurrencyCode, Order } from "@/types";
import { isSalesOrder } from "@/utils/Orders";
import { useAuth } from "@/contexts/AuthContext";
import Stack from "@/components/atoms/Stack";
import Group from "@/components/atoms/Group";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import styles from "./styles.module.css";

export interface OrderCardProps {
  order: Order;
  handleActiveOrder: (Order: any) => void;
  deleteOrder: (orderId: number) => void;
}

export default function OrderCard(props: OrderCardProps) {
  const { t } = useTranslation();
  const { order, handleActiveOrder, deleteOrder } = props;
  const { user, currentCompany, isViewer } = useAuth();

  function handleDetails() {
    handleActiveOrder(order);
  }

  function handleCancel() {
    deleteOrder(order.id);
  }

  const salesTypeIcon = isSalesOrder(order) ? "salesIndicatorLarge" : "purchaseIndicatorLarge";
  const currency = (currentCompany || {}).companyCurrency;
  const priceText = `${order.price?.[currency as CurrencyCode]} ${currency}/${order?.priceUnit?.unit || ""}`;
  const amountText = `${order.remainingAmount} ${order.unit?.unit || ""}`;

  return (
    <>
      <section className={styles.activeOrderCardRoot}>
        <header>
          <Group justify="space-between" align="flex-start">
            <Group justify="flex-start" gap="12px">
              <Stack gap={0}>
                <Text size="lg" display="technical" strong={true}>
                  {user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
                    ? order.salesItem.nameTranslated[user.language.id]
                    : order.salesItem.salesItem}
                </Text>
                <Text size="sm" display="technical">
                  {priceText}
                </Text>
                <Text size="sm" display="technical">
                  {amountText}
                </Text>
              </Stack>
            </Group>
            <SVGAsset name={salesTypeIcon} width="24" height="24" />
          </Group>
        </header>
        <ul className={styles.productSpecs}>
          {[...(order.salesItem.salesItemProperties ?? [])]
            .sort((a, b) => a.salesItemPropertyId - b.salesItemPropertyId)
            .map((salesItemProperty) => {
              const matchingProperty = order.properties?.find((p) => p.index === salesItemProperty.salesItemPropertyId);
              const label =
                salesItemProperty.salesItemPropertyTranslated.find((t) => t.languageId === user?.language.id)
                  ?.propertyTranslated ?? salesItemProperty.salesItemPropertyName;

              return (
                <li key={salesItemProperty.salesItemPropertyId}>
                  <Text size="xs" color="secondary" tt="uppercase">
                    {label}
                  </Text>
                  <Text size="md" strong={true}>
                    {matchingProperty?.value ?? "-"}
                  </Text>
                </li>
              );
            })}
        </ul>
        <div className={styles.ctas}>
          <CTA intent="secondary" disabled={isViewer(currentCompany)} onClick={handleCancel}>
            {t("orders:cancel")}
          </CTA>
          <CTA intent="primary" onClick={handleDetails}>
            {t("common:details")}
          </CTA>
        </div>
      </section>
    </>
  );
}
